import { useEffect, useState } from 'react';

import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { Commit } from 'src/components/types';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { SignatureStatus, VerificationStatus } from 'src/types';
import urls from 'src/urls/commit';
import authRequest, { jsonHeaders } from 'src/utils/fetch';

export function isValidSignature(status: SignatureStatus): boolean {
  return (
    status === SignatureStatus.VALID ||
    status === SignatureStatus.VALID_BUT_OUT_OF_DATE ||
    status === SignatureStatus.VALID_BUT_MADE_AFTER_EXPIRY ||
    status === SignatureStatus.VALID_BUT_REVOKED
  );
}

function normalizeKeyID(keyID: string) {
  return keyID.length === 15 ? `${keyID}0` : keyID;
}

export function formatKeyID(keyID: string | undefined) {
  if (typeof keyID !== 'string') {
    return '';
  }

  const normalizedKeyID = normalizeKeyID(keyID);

  if (normalizedKeyID.length !== 16) {
    return '';
  }

  return normalizedKeyID
    .toUpperCase()
    .match(/.{1,4}/g)
    ?.join(' ');
}

export function useVerificationStatus(commit: Commit) {
  const hasGpgKeyFeature = useStatsigGate(StatsigFeatureKeys.gpgKeysUi);
  const [verification, setVerification] = useState<VerificationStatus | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVerificationStatus = async () => {
      try {
        setLoading(true);
        const { hash } = commit;
        const fullSlug = commit.repository.full_name;

        const url = urls.api.internal.verificationStatuses(fullSlug);
        const hashes = [hash];
        const body = JSON.stringify({ hashes });
        const request = authRequest(url, {
          method: 'POST',
          headers: jsonHeaders,
          body,
        });
        const response = await fetch(request);
        if (!response.ok) {
          throw new Error('Failed to fetch verification status');
        }
        const data: VerificationStatus[] = await response.json();
        setVerification(data[0] || null);
      } catch (err) {
        setError(err.message || 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    };
    if (hasGpgKeyFeature) {
      fetchVerificationStatus();
    }
  }, [commit, hasGpgKeyFeature]);

  return { verification, loading, error };
}
